import { ApolloError } from '@apollo/client/errors'
import { GetUserLocationDocument } from '@kijiji/generated/graphql-types'
import { type GetServerSideProps, type Redirect } from 'next'
import { getSession } from 'next-auth/react'
import { v4 as generateUUID } from 'uuid'

import {
  getListingHomepageGallery,
  getListingHomepageGalleryVariables,
} from '@/components/homepage/homepage-gallery/getHomepageGallery'
import { HomepageInner } from '@/components/homepage/homepage-inner/HomepageInner'
import { getPopularNearYouLinks } from '@/components/homepage/popular-near-you/getPopularNearYouLinks'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { debugServerTime } from '@/domain/debugServerTiming'
import { initializePageServer } from '@/domain/initializePageServer'
import { isValidLocation } from '@/domain/location/isValidLocation'
import { getListingsMostRecent } from '@/features/listing/components/galleries/most-recent-listings/utils/getListingsMostRecent'
import { getMenuPrefetch } from '@/features/navigation/api/getMenuPrefetch'
import { addApolloState } from '@/lib/apollo/apolloClient'
import profile from '@/lib/metrics/profileWrapper'
import { type BasePageProps } from '@/pages/_app'

interface HomePageProps extends BasePageProps {
  correlationId: string
}

/**
 * The homepage could be called by accessing `/`
 * or paths like: `/h-<location-seo-name>/<locationId>
 *
 * When there are parameters on the homepage path, then the user's location
 * should be switched to that defined location
 */
const Home = ({ correlationId, userAgent = '' }: HomePageProps) => {
  return <HomepageInner correlationId={correlationId} userAgent={userAgent} />
}

export const getServerSideProps: GetServerSideProps<HomePageProps> = profile<
  GetServerSideProps<HomePageProps>
>('homepage', 'getServerSideProps', async (context) => {
  const gSSPstart = performance.now()
  const { res, req } = context

  const {
    basePageProps,
    apolloClient,
    locale: { cookieLocale },
  } = await initializePageServer('homepage', context, [
    TRANSLATION_KEYS.HOME,
    TRANSLATION_KEYS.FAVOURITES,
    TRANSLATION_KEYS.LISTING,
  ])

  /**
   * Queries we want to pre-fetch on the server to load asap in the page
   * */
  const location = apolloClient.readQuery({ query: GetUserLocationDocument })
  const locationId = location?.userLocation.id

  const correlationId = generateUUID()

  // Note: isValidLocation doesn't actually check if location is valid, just that the id is a number
  if (!isValidLocation(locationId)) {
    return { redirect: { destination: '/h-canada/0', permanent: false } }
  }

  const userAgent = req.headers['user-agent'] ?? ''
  const homepageGalleryVariables = getListingHomepageGalleryVariables(userAgent, locationId, 0)

  const session = await getSession(context)
  const isAuthenticated = !!session

  const queryResults = await Promise.allSettled([
    profile(
      'homepage',
      'getMenuPrefetch',
      getMenuPrefetch
    )({ apolloClient, cookieLocale, locationId }),

    // Only server-side fetch these queries if the user is logged out (for SEO indexing)
    ...(!isAuthenticated
      ? [
          profile(
            'homepage',
            'homepageGallery',
            getListingHomepageGallery
          )({
            apolloClient,
            variables: homepageGalleryVariables,
          }),

          profile(
            'homepage',
            'getMostRecentListings',
            getListingsMostRecent
          )({
            apolloClient,
            locationId,
          }),
          profile(
            'homepage',
            'getPopularNearYouLinks',
            getPopularNearYouLinks
          )({ apolloClient, locationId }),
        ]
      : []),
  ])

  let locationErrorRedirect: { redirect: Redirect } | null = null

  queryResults.forEach((result) => {
    if (
      result.status !== 'rejected' ||
      !(result.reason instanceof ApolloError && result.reason.graphQLErrors.length > 0)
    ) {
      return
    }

    const errorCode = result.reason.graphQLErrors[0].extensions?.code
    const errorMessage = result.reason.graphQLErrors[0].message

    if (errorCode === 'LOCATION_ERROR' || errorMessage.includes('location')) {
      locationErrorRedirect = { redirect: { destination: '/h-canada/0', permanent: false } }
    }
  })

  if (locationErrorRedirect) {
    return locationErrorRedirect
  }

  const pageProps: HomePageProps = {
    correlationId,
    ...basePageProps,
  }

  const props = profile('homepage', 'addApolloState', addApolloState)(apolloClient, pageProps)
  debugServerTime('props', res, gSSPstart)

  return { props }
})

export default Home
